import { makeApolloClient } from "@ticketingplatform/api/dist/config/apollo";
import { fetchAuthSession } from "aws-amplify/auth";
import { setContext } from "@apollo/client/link/context";
import { Venue } from "@ticketingplatform/api/dist/venue/__generated__/fragments";

import environment from "./environment";
import { LOCAL_STORAGE_VENUE_KEY } from "./consts";

const getAuthHeaders = async (): Promise<Record<string, string>> => {
  const defaultHeaders: { Authorization: string } | { "x-api-key": string } =
    environment.LOCAL_TOKEN
      ? {
          Authorization: environment.LOCAL_TOKEN,
        }
      : {
          "x-api-key": environment.APPSYNC_KEY,
        };

  try {
    // fetchAuthSession returns valid (auto-refreshed) tokens, or empty when
    // unauthenticated. Keep the access token to match the AppSync authorizer.
    const { tokens } = await fetchAuthSession();
    const accessToken = tokens?.accessToken?.toString();

    return accessToken ? { Authorization: accessToken } : defaultHeaders;
  } catch {
    return defaultHeaders;
  }
};

const getVenueHeaders = () => {
  if (typeof window === "undefined") {
    return {};
  }

  const venueJson = window.localStorage.getItem(LOCAL_STORAGE_VENUE_KEY);

  if (!venueJson) {
    return {};
  }

  const venue: Venue | undefined = JSON.parse(venueJson);

  if (!venue) {
    return {};
  }

  return { "x-venue-id": venue.id };
};

const authLink = setContext(async (operation, prevContext) => {
  const authHeaders = await getAuthHeaders();

  const context = {
    ...operation.context,
    ...prevContext,
  };

  return {
    ...(context || {}),
    headers: {
      ...(context?.headers || {}),
      ...authHeaders,
    },
  };
});

const venueLink = setContext((operation, prevContext) => {
  const venueHeaders = getVenueHeaders();

  const context = {
    ...operation.context,
    ...prevContext,
  };

  return {
    ...(context || {}),
    headers: {
      ...(context?.headers || {}),
      ...venueHeaders,
    },
  };
});

export const apolloClient = makeApolloClient([authLink, venueLink]);
